// 9fbef606107a605d69c0edbcd8029e5d
import AccountCommonLabels, {
  ACCOUNT_COMMON_LABELS,
  lblCommonPoints,
  lblMyRewardUnavailable,
  PLACE_REWARDS_LABELS,
  CHECKOUT_LABELS,
} from '@tcp/core/src/components/features/account/common/labels/AccountCommon.labels';
import LOYALTY_PLACE_REWARDS_CONSTANTS from '../components/features/account/PlaceRewards/LoyaltyPlaceRewards.constants';
import LOYALTY_THEME_KEY from '../constants/loyaltyTiers.constants';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
  lblCurrency,
} from './labels/globalGeneric.labels';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from './labels/labelUtilty';

export const getContentID = (keyName) => {
  const referredContent = getDynamicLabelValues('global.accountTier.referred');
  let content;
  if (referredContent && Array.isArray(referredContent)) {
    content = referredContent.find((label) => label.name === keyName);
    if (content && content.contentId) {
      return content.contentId;
    }
  }
  return null;
};

export const getCurrencyLabel = () => {
  return getLabelFromCopyOrFromTranslation(
    `${GLOBAL_GENERIC_LABELS}.${lblCurrency}`,
    GlobalGenericLabels
  );
};

export const getAvailableRewards = (currentPoints, rewardsDollarPoints, rewardsDollarAmount) => {
  const rewardAmount = Math.floor(currentPoints / rewardsDollarPoints) * rewardsDollarAmount;
  return `${getCurrencyLabel()}${rewardAmount || 0}`;
};

// Assuming the input date format is "YYYY-MM-DD"
export const getExpiryDate = (expirationDate) => {
  if (!expirationDate) return '';
  const [year, month, day] = expirationDate.split('-');
  return `${month}/${day}/${year}`;
};
export const formateDateTwoDigitYear = (date) => {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year.slice(-2)}`;
};

export const getTargetSpend = (loyaltyInformation) => {
  const targetSpend = loyaltyInformation?.get('targetSpendForNextTier');
  return `${getCurrencyLabel()}${targetSpend || 0}`;
};

export const getNextUpgradableTierName = (currentTier, loyaltyTierValues) => {
  if (!currentTier) return '';
  if (currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER1]) {
    return loyaltyTierValues[LOYALTY_THEME_KEY?.TIER2];
  }
  if (currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER2]) {
    return loyaltyTierValues[LOYALTY_THEME_KEY?.TIER3];
  }
  return currentTier;
};

export const getNextUpgradableTierKey = (currentTier, loyaltyTierValues) => {
  if (!currentTier) return '';
  if (currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER1]) {
    return LOYALTY_THEME_KEY?.TIER2;
  }
  if (currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER2]) {
    return LOYALTY_THEME_KEY?.TIER3;
  }
  return currentTier;
};

export const getTierKey = (loyaltyTierValues, currentTier) => {
  return Object.keys(loyaltyTierValues).find((key) => loyaltyTierValues[key] === currentTier);
};

export const isDirectTierUpgrade = (
  totalSpend,
  isPlcc,
  tierUpgradeValues,
  currentTier,
  loyaltyTierValues
) => {
  const tierUpgradeTarget = tierUpgradeValues[getTierKey(loyaltyTierValues, currentTier)];
  if (tierUpgradeTarget)
    return (
      totalSpend < tierUpgradeTarget &&
      isPlcc &&
      currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER2]
    );
  return false;
};

export const getProgressToUpgrade = (
  tierUpgradeValues,
  loyaltyInformation,
  totalSpend,
  isPlcc,
  isMyPlaceRewardPage = false,
  loyaltyTierValues
) => {
  const currentTier = loyaltyInformation?.get('currentTier');
  if (currentTier === loyaltyTierValues[LOYALTY_THEME_KEY?.TIER3]) {
    // If current tier is already maximum then progress should be 100%
    return 100;
  }
  const directTierUpgrade = isDirectTierUpgrade(
    totalSpend,
    isPlcc,
    tierUpgradeValues,
    currentTier,
    loyaltyTierValues
  );

  const nextTier = getNextUpgradableTierKey(
    loyaltyInformation?.get('currentTier'),
    loyaltyTierValues
  );
  const nextTierUpgradeValue = parseInt(tierUpgradeValues[nextTier], 10);

  let tierProgress = 0; // In case of any exception default tier progress will be 0%
  if (nextTierUpgradeValue && typeof nextTierUpgradeValue === 'number') {
    // Use totalSpend if its direct tier-2 upgrade rather calculating it.
    const currentSpend = directTierUpgrade
      ? totalSpend
      : nextTierUpgradeValue - loyaltyInformation?.get('targetSpendForNextTier');
    if (currentSpend && typeof currentSpend === 'number') {
      tierProgress = (currentSpend / nextTierUpgradeValue) * 100 || 0;
    }
  }
  if (isMyPlaceRewardPage) {
    // for my place reward page progress bar shows progress till top tier with midway tier milestones
    const milestoneProgress =
      ((nextTierUpgradeValue / tierUpgradeValues?.Tier3) * 100 * tierProgress) / 100;
    return milestoneProgress || 0;
  }
  return tierProgress;
};

export const getMidMilestoneDistance = (tierUpgradeValues) => {
  return (tierUpgradeValues?.Tier2 / tierUpgradeValues?.Tier3) * 100;
};

export const getBenefitProgress = (usedCount, totalCount) => {
  return (usedCount / totalCount) * 100;
};

export const getDaysToExpiration = (expirationDate) => {
  const today = new Date();
  const expiration = new Date(expirationDate);
  const timeDifference = expiration - today;
  return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
};

export const getBenefitStatus = (
  expirationDate,
  benefitExpirationDays,
  usedCount,
  totalCount,
  isExpired
) => {
  if (usedCount === totalCount) {
    return LOYALTY_PLACE_REWARDS_CONSTANTS?.BENEFIT_REDEEMED;
  }
  if (isExpired) {
    return LOYALTY_PLACE_REWARDS_CONSTANTS?.BENEFIT_EXPIRED;
  }
  const daysToExpiration = getDaysToExpiration(expirationDate);
  if (daysToExpiration <= benefitExpirationDays) {
    return LOYALTY_PLACE_REWARDS_CONSTANTS?.BENEFIT_EXPIRING_SOON;
  }
  return '';
};

export const getPlccProgressWidth = (
  tierUpgradeValues,
  loyaltyInformation,
  totalSpend,
  isPlcc,
  isMyPlaceRewardPage = false,
  loyaltyTierValues
) => {
  return isPlcc
    ? getMidMilestoneDistance(tierUpgradeValues) -
        getProgressToUpgrade(
          tierUpgradeValues,
          loyaltyInformation,
          totalSpend,
          isPlcc,
          isMyPlaceRewardPage,
          loyaltyTierValues
        )
    : 0;
};

export const formatDisplayName = (rewardValue, rewardPoints, pointsToRedeem = 0) => {
  return `${getCurrencyLabel()}${rewardValue} (${rewardPoints} ${getLabelFromCopyOrFromTranslation(
    `${ACCOUNT_COMMON_LABELS}.${lblCommonPoints}`,
    AccountCommonLabels
  )}${pointsToRedeem === rewardPoints ? ' Redeemed' : ''})`;
};

export const getPointsToRewards = (pointsValue, loyaltyPointsMultiplier) => {
  const { rewardsDollarPoints, rewardsDollarAmount } = loyaltyPointsMultiplier;
  const rewardAmount = Math.floor(pointsValue / rewardsDollarPoints) * rewardsDollarAmount;
  return rewardAmount || 0;
};

export const getUsedAmount = (redeemablePoints, dailyPointRedeemLimit, loyaltyPointsMultiplier) => {
  const redeemedPoints = dailyPointRedeemLimit - redeemablePoints;
  return getPointsToRewards(redeemedPoints, loyaltyPointsMultiplier);
};

export const getRewardsBalance = (currentPoints, appliedRewardPoints, loyaltyPointsMultiplier) => {
  const availablePoints = currentPoints - appliedRewardPoints;
  const rewardBalance = getPointsToRewards(availablePoints, loyaltyPointsMultiplier);
  return rewardBalance > 0 ? rewardBalance : 0;
};

export const getNoRewardLabel = () => {
  return getLabelFromCopyOrFromTranslation(
    `${PLACE_REWARDS_LABELS}.${lblMyRewardUnavailable}`,
    AccountCommonLabels
  );
};

export const calculateRedeemOptions = (
  currentPoints,
  loyaltyPointsMultiplier,
  dailyPointRedeemLimit,
  summaryData,
  pointsToRedeem
) => {
  try {
    const options = [];
    const { subTotal } = summaryData;
    const { rewardsDollarPoints, rewardsDollarAmount } = loyaltyPointsMultiplier;
    const totalRedeemablePoints =
      currentPoints < dailyPointRedeemLimit ? currentPoints : dailyPointRedeemLimit;
    if (!totalRedeemablePoints || totalRedeemablePoints < rewardsDollarPoints) {
      return [
        {
          id: 0,
          value: 0,
          displayName: getNoRewardLabel(),
          enabled: true,
        },
      ];
    }
    if (typeof totalRedeemablePoints !== 'number' || totalRedeemablePoints < 0) {
      throw new Error('Invalid input: totalPoints must be a non-negative number.');
    }

    const numberOfRewards = Math.floor(totalRedeemablePoints / rewardsDollarPoints);

    for (let i = 1; i <= numberOfRewards; i += 1) {
      const rewardValue = i * rewardsDollarAmount;
      const rewardPoints = i * rewardsDollarPoints;
      options.push({
        id: rewardPoints,
        value: rewardValue,
        displayName: formatDisplayName(rewardValue, rewardPoints, pointsToRedeem),
        enabled: rewardValue < subTotal,
      });
    }

    return options.reverse();
  } catch (error) {
    return [];
  }
};

export const getCartApplicableTotal = (cartOrderData, summaryData) => {
  const { subTotalWithDiscounts } = cartOrderData;
  const { giftServiceTotal, giftDiscountTotal } = summaryData;
  return subTotalWithDiscounts - giftServiceTotal - giftDiscountTotal;
};

export const getPointBalance = (currentPoints = 0, appliedRewardPoints = 0) => {
  return currentPoints - appliedRewardPoints;
};

export const redemptionButtonState = (
  currentPoints,
  selectedReward,
  defaultSelectedValue,
  displayError,
  appliedRewardPoints
) => {
  return (
    currentPoints === 0 ||
    displayError ||
    appliedRewardPoints === (selectedReward || defaultSelectedValue)
  );
};

export const calculateErrorMessage = (
  usedAmountValue,
  selectOption,
  dailyLimitReward,
  rewardOutOfLimit
) => {
  const checkoutLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_LABELS, AccountCommonLabels);
  if (!selectOption?.enabled || rewardOutOfLimit) {
    return renderLabel(checkoutLabels?.bagPage?.lbl_cart_amount_error, 'lbl_cart_amount_error');
  }
  const currencySymbol = getCurrencyLabel();
  if (usedAmountValue === dailyLimitReward) {
    return renderLabel(checkoutLabels?.bagPage?.lbl_consumed, 'lbl_consumed').replace(
      '##dollarValue##',
      currencySymbol + dailyLimitReward
    );
  }
  if (usedAmountValue + selectOption.value > dailyLimitReward) {
    return renderLabel(checkoutLabels?.bagPage?.lbl_consume_up_to, 'lbl_consume_up_to')
      .replace('##usedAmount##', currencySymbol + usedAmountValue)
      .replace('##remainingAmount##', currencySymbol + (dailyLimitReward - usedAmountValue));
  }
  return '';
};

export const displayEmptyToNA = (currentTier, displayValue) => {
  return currentTier ? displayValue : 'NA';
};
